import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth';

export default function CheckEmailPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');

  const handleResend = async () => {
    if (!email) {
      setResendStatus('error');
      setResendMessage('No email on file. Return to sign up to register.');
      return;
    }
    setResendStatus('sending');
    setResendMessage('');
    try {
      await authService.resendVerificationByEmail(email);
      setResendStatus('sent');
      setResendMessage('Verification email sent. Check your inbox (and spam folder).');
    } catch {
      setResendStatus('error');
      setResendMessage('Could not resend verification email. Please try again in a moment.');
    }
  };

  return (
    <>
      <p className="mt-2 text-center text-sm text-slate-500">Verify your email</p>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-sm border border-slate-100 rounded-2xl sm:px-10 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-xl bg-blue-50 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>

          <h1 className="text-lg font-semibold text-slate-900">Check your email</h1>
          <p className="mt-2 text-sm text-slate-500">
            We sent a verification link to{' '}
            {email ? <span className="font-semibold text-slate-700">{email}</span> : 'your inbox'}.
            Click the link to activate your account before signing in.
          </p>

          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendStatus === 'sending'}
              className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm shadow-blue-500/25 disabled:opacity-50 transition-all"
            >
              {resendStatus === 'sending' ? 'Sending...' : 'Resend verification email'}
            </button>

            <Link
              to="/login"
              className="block w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-slate-700 border-2 border-slate-200 hover:bg-slate-50 transition-colors"
            >
              Back to sign in
            </Link>
          </div>

          {resendStatus === 'sent' && (
            <p className="mt-4 text-sm text-emerald-600 font-medium">{resendMessage}</p>
          )}
          {resendStatus === 'error' && (
            <p className="mt-4 text-sm text-red-600 font-medium">{resendMessage}</p>
          )}

          <p className="mt-6 text-xs text-slate-400">
            Didn't get the email? Check your spam folder, or click resend above.
          </p>
        </div>
      </div>
    </>
  );
}
